import {
  Alert,
  Badge,
  Button,
  Card,
  FileInput,
  Group,
  Image,
  Modal,
  Select,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconAlertTriangle, IconRobot } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import {
  useAddCaseImage,
  useAiReviews,
  useCaseImages,
  useConfirmCaseImage,
  useRemoveCaseImage,
  useRetryCaseImage,
  useTriggerAiReview,
  useTriggerAiReviewForFinding,
  type AdminCaseImage,
  type AdminFinding,
  type AiReviewSummary,
} from '../api/cases';
import { parseMutationError } from '../api/errors';
import { useHasPermission } from '../api/session';
import { isTerminal, statusLabel } from './ai-review-status';
import { DermoscopyExceptionFields } from './dermoscopy-exception-fields';
import { FindingCard } from './finding-card';
import { reviewsUsingImage, uploaderLabel } from './image-provenance';
import { ReasonConfirmModal } from './reason-confirm-modal';

const CAPTURE_TYPES = [
  { value: 'dermoscopic', label: 'Dermoscopic' },
  { value: 'macroscopic', label: 'Macroscopic' },
  { value: 'other', label: 'Other' },
];

function findingProcessedImages(f: AdminFinding) {
  return f.images.filter((img) => img.ingestion_status === 'processed');
}

function findingIsMacroOnly(f: AdminFinding): boolean {
  const processed = findingProcessedImages(f);
  return processed.length > 0 && !processed.some((img) => img.capture_type === 'dermoscopic');
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

// Stable fingerprint of a finding's reviews, used to detect when the engine
// state for that finding has changed since an optimistic trigger — including a
// straight-to-terminal transition the client never observed as pending.
function reviewSignature(reviews: AiReviewSummary[], findingId: string): string {
  return reviews
    .filter((r) => r.findingId === findingId)
    .map((r) => `${r.id}:${r.status}:${r.completedAt ?? ''}`)
    .join('|');
}

export interface CaseHeaderInfo {
  id: string;
  patientName: string;
  orgName: string;
  productName: string;
}

interface Props {
  caseId: string;
  findings: AdminFinding[];
  caseInfo: CaseHeaderInfo;
}

/**
 * Orchestrator for the per-finding case-detail redesign. Fetches images and
 * AI reviews for the whole case, groups them per finding, and renders one
 * FindingCard each. Owns every triage modal (per-lesion + run-all AI trigger,
 * add / remove / retry image, zoom) and the optimistic "just triggered"
 * feedback so a queued lesion shows immediately, before the reviews poll
 * catches up.
 */
export function CaseFindingsSection({ caseId, findings, caseInfo }: Props) {
  const canTriage = useHasPermission('case_triage:write');
  // Optimistic "just triggered" markers, keyed by finding id. Declared before
  // useAiReviews so the query can keep polling while any marker is active (hook
  // call order must stay stable).
  const [justTriggered, setJustTriggered] = useState<Record<string, { at: number; sig: string }>>(
    {},
  );
  const imagesQuery = useCaseImages(caseId);
  const reviewsQuery = useAiReviews(caseId, Object.keys(justTriggered).length > 0);
  const triggerFindingReview = useTriggerAiReviewForFinding(caseId);
  const triggerAll = useTriggerAiReview(caseId);
  const addImage = useAddCaseImage(caseId);
  const confirmImage = useConfirmCaseImage(caseId);
  const removeImage = useRemoveCaseImage(caseId);
  const retryImage = useRetryCaseImage(caseId);
  const queryClient = useQueryClient();

  const images = useMemo(() => imagesQuery.data?.items ?? [], [imagesQuery.data]);
  const reviews = useMemo(() => reviewsQuery.data?.data ?? [], [reviewsQuery.data]);

  const imagesByFinding = (id: string) => images.filter((i) => i.finding_id === id);
  const reviewsByFinding = (id: string) =>
    reviews
      .filter((r) => r.findingId === id)
      .slice()
      .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1)); // newest first

  const anyInflight = reviews.some((r) => !isTerminal(r.status));

  // --- Optimistic "just triggered" feedback -------------------------------
  // (state declared above, before useAiReviews, to keep polling alive)

  const markTriggered = (findingId: string) =>
    setJustTriggered((m) => ({
      ...m,
      [findingId]: { at: Date.now(), sig: reviewSignature(reviews, findingId) },
    }));

  const clearTriggered = (findingId: string) =>
    setJustTriggered((m) => {
      if (m[findingId] == null) return m;
      const next = { ...m };
      delete next[findingId];
      return next;
    });

  // Drop a finding's optimistic marker once its reviews signature changes from
  // the snapshot captured at trigger time — this catches straight-to-terminal
  // transitions the client never observed as pending. Only writes when
  // something actually changes so the effect can't loop.
  useEffect(() => {
    setJustTriggered((m) => {
      let changed = false;
      const next = { ...m };
      for (const id of Object.keys(m)) {
        if (reviewSignature(reviews, id) !== m[id].sig) {
          delete next[id];
          changed = true;
        }
      }
      return changed ? next : m;
    });
  }, [reviews]);

  // --- Per-lesion AI-review trigger ---------------------------------------
  const [aiFindingId, setAiFindingId] = useState<string | null>(null);
  const [aiReason, setAiReason] = useState<string | null>(null);
  const [aiNote, setAiNote] = useState('');
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiConfirmAttempted, setAiConfirmAttempted] = useState(false);
  const aiNoteRequiredButMissing = aiReason === 'other' && aiNote.trim().length === 0;

  function openTriggerFor(findingId: string) {
    setAiFindingId(findingId);
    setAiReason(null);
    setAiNote('');
    setAiError(null);
    setAiConfirmAttempted(false);
  }

  const aiTarget = findings.find((f) => f.id === aiFindingId) ?? null;
  const aiMacroOnly = aiTarget ? findingIsMacroOnly(aiTarget) : false;

  function handleConfirmTriggerFinding(reason: string) {
    if (!aiFindingId) return;
    if (aiMacroOnly && (!aiReason || aiNoteRequiredButMissing)) {
      setAiConfirmAttempted(true);
      return;
    }
    setAiError(null);
    const findingId = aiFindingId;
    markTriggered(findingId);
    triggerFindingReview.mutate(
      {
        findingId,
        reason,
        dermoscopyExceptionReason: aiMacroOnly ? (aiReason ?? undefined) : undefined,
        dermoscopyExceptionNote: aiMacroOnly && aiReason === 'other' ? aiNote.trim() : undefined,
      },
      {
        onSuccess: () => setAiFindingId(null),
        onError: (err) => {
          clearTriggered(findingId);
          setAiError(parseMutationError(err, 'Failed to trigger AI review.'));
        },
      },
    );
  }

  // --- Run-all AI-review trigger ------------------------------------------
  const [runAllOpen, setRunAllOpen] = useState(false);
  const [runAllReason, setRunAllReason] = useState<string | null>(null);
  const [runAllNote, setRunAllNote] = useState('');
  const [runAllError, setRunAllError] = useState<string | null>(null);
  const runAllNoteRequiredButMissing = runAllReason === 'other' && runAllNote.trim().length === 0;

  function resetRunAllFields() {
    setRunAllReason(null);
    setRunAllNote('');
  }

  function openRunAll() {
    resetRunAllFields();
    setRunAllError(null);
    setRunAllOpen(true);
  }

  function handleConfirmRunAll(reason: string) {
    if (runAllNoteRequiredButMissing) return;
    setRunAllError(null);
    triggerAll.mutate(
      {
        reason,
        dermoscopyExceptionReason: runAllReason ?? undefined,
        dermoscopyExceptionNote: runAllReason === 'other' ? runAllNote.trim() : undefined,
      },
      {
        onSuccess: () => {
          setRunAllOpen(false);
          resetRunAllFields();
          for (const f of findings) {
            if (findingProcessedImages(f).length > 0) markTriggered(f.id);
          }
          void queryClient.invalidateQueries({ queryKey: ['cases', 'ai-reviews', caseId] });
        },
        onError: (err) => setRunAllError(parseMutationError(err, 'Failed to trigger AI review.')),
      },
    );
  }

  // --- Add image ----------------------------------------------------------
  const [addOpen, { open: openAddRaw, close: closeAddRaw }] = useDisclosure(false);
  const [addFindingId, setAddFindingId] = useState<string | null>(null);
  const [addFile, setAddFile] = useState<File | null>(null);
  const [addCaptureType, setAddCaptureType] = useState<string | null>(null);
  const [addConfirmOpen, setAddConfirmOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  function openAddFor(findingId: string) {
    setAddFindingId(findingId);
    setAddFile(null);
    setAddCaptureType(null);
    setAddError(null);
    openAddRaw();
  }

  function closeAdd() {
    setAddFindingId(null);
    setAddFile(null);
    setAddCaptureType(null);
    setAddError(null);
    closeAddRaw();
  }

  function handleContinueToReason() {
    if (!addFindingId || !addFile || !addCaptureType) return;
    closeAddRaw();
    setAddConfirmOpen(true);
  }

  function handleConfirmAdd(reason: string) {
    if (!addFindingId || !addFile || !addCaptureType) return;
    setAddError(null);
    addImage.mutate(
      {
        findingId: addFindingId,
        mimeType: addFile.type,
        captureType: addCaptureType as 'dermoscopic' | 'macroscopic' | 'other',
        reason,
      },
      {
        onSuccess: async ({ image_id: imageId, upload_url: uploadUrl }) => {
          try {
            const putRes = await fetch(uploadUrl, {
              method: 'PUT',
              body: addFile,
              headers: { 'Content-Type': addFile.type },
            });
            if (!putRes.ok) throw new Error(`Upload failed with status ${putRes.status}`);
            confirmImage.mutate(
              { findingId: addFindingId, imageId },
              {
                onSuccess: (confirmed) => {
                  if (confirmed.ingestion_status !== 'processed') {
                    setAddError(
                      `Upload succeeded but the image was rejected during processing (${confirmed.ingestion_status}). It has not been added to the case.`,
                    );
                    return;
                  }
                  setAddConfirmOpen(false);
                  closeAdd();
                },
                onError: (err) =>
                  setAddError(
                    parseMutationError(
                      err,
                      'Upload succeeded but the image could not be confirmed. It has not been added to the case — check storage/permissions and try adding it again, or contact support if this persists.',
                    ),
                  ),
              },
            );
          } catch (err) {
            setAddError(err instanceof Error ? err.message : 'Upload to storage failed.');
          }
        },
        onError: (err) => setAddError(parseMutationError(err, 'Could not register the new image.')),
      },
    );
  }

  // --- Remove image -------------------------------------------------------
  const [removeTarget, setRemoveTarget] = useState<AdminCaseImage | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);

  function handleConfirmRemove(reason: string) {
    if (!removeTarget) return;
    setRemoveError(null);
    removeImage.mutate(
      { findingId: removeTarget.finding_id, imageId: removeTarget.id, reason },
      {
        onSuccess: () => setRemoveTarget(null),
        onError: (err) => setRemoveError(parseMutationError(err, 'Could not remove image.')),
      },
    );
  }

  // --- Retry image --------------------------------------------------------
  const [retryTarget, setRetryTarget] = useState<{ findingId: string; imageId: string } | null>(
    null,
  );
  const [retryError, setRetryError] = useState<string | null>(null);

  function handleConfirmRetry(reason: string) {
    if (!retryTarget) return;
    setRetryError(null);
    retryImage.mutate(
      { findingId: retryTarget.findingId, imageId: retryTarget.imageId, reason },
      {
        onSuccess: () => setRetryTarget(null),
        onError: (err) => setRetryError(parseMutationError(err, 'Could not retry ingestion.')),
      },
    );
  }

  // --- Zoom ---------------------------------------------------------------
  const [zoomImage, setZoomImage] = useState<AdminCaseImage | null>(null);
  const [zoomOpen, { open: openZoom, close: closeZoom }] = useDisclosure(false);

  function handleZoom(image: AdminCaseImage) {
    setZoomImage(image);
    openZoom();
  }

  const zoomImageReviews = zoomImage ? reviewsUsingImage(reviews, zoomImage.id) : [];

  return (
    <Card withBorder padding="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Title order={5}>Findings &amp; AI review</Title>
          {canTriage && findings.length > 0 && (
            <Button
              size="xs"
              leftSection={<IconRobot size={14} />}
              loading={triggerAll.isPending}
              disabled={reviewsQuery.isLoading || anyInflight}
              onClick={openRunAll}
            >
              Run AI review on all lesions
            </Button>
          )}
        </Group>

        {imagesQuery.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load images.
          </Alert>
        )}
        {reviewsQuery.isError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            Could not load AI reviews.
          </Alert>
        )}
        {removeError && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {removeError}
          </Alert>
        )}
        {addError && !addConfirmOpen && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {addError}
          </Alert>
        )}
        {retryError && !retryTarget && (
          <Alert color="red" icon={<IconAlertTriangle size={18} />}>
            {retryError}
          </Alert>
        )}

        {findings.length === 0 ? (
          <Text c="dimmed">No findings recorded for this case.</Text>
        ) : (
          findings.map((f) => (
            <FindingCard
              key={f.id}
              finding={f}
              thumbnails={imagesByFinding(f.id)}
              reviews={reviewsByFinding(f.id)}
              optimisticQueued={justTriggered[f.id] != null}
              canTriage={canTriage}
              onRunReview={openTriggerFor}
              onAddImage={openAddFor}
              onZoomImage={handleZoom}
              onRemoveImage={setRemoveTarget}
              onRetryImage={(findingId, imageId) => setRetryTarget({ findingId, imageId })}
            />
          ))
        )}
      </Stack>

      {/* Per-lesion AI-review trigger */}
      <ReasonConfirmModal
        opened={aiFindingId !== null}
        title="Confirm action"
        description="This runs an AI review for the selected lesion."
        confirmLabel="Confirm"
        isPending={triggerFindingReview.isPending}
        error={aiError}
        onConfirm={handleConfirmTriggerFinding}
        onClose={() => {
          setAiFindingId(null);
          setAiError(null);
          setAiConfirmAttempted(false);
        }}
      >
        {aiMacroOnly && (
          <DermoscopyExceptionFields
            reason={aiReason}
            note={aiNote}
            onReasonChange={setAiReason}
            onNoteChange={setAiNote}
            noteRequiredButMissing={aiNoteRequiredButMissing}
            reasonRequiredButMissing={aiConfirmAttempted && aiMacroOnly && !aiReason}
            disabled={triggerFindingReview.isPending}
          />
        )}
      </ReasonConfirmModal>

      {/* Run-all AI-review trigger */}
      <ReasonConfirmModal
        opened={runAllOpen}
        title="Confirm action"
        description="This runs an AI review across every lesion on this case, even ones already reviewed."
        confirmLabel="Confirm"
        isPending={triggerAll.isPending}
        error={runAllError}
        onConfirm={handleConfirmRunAll}
        onClose={() => {
          setRunAllOpen(false);
          setRunAllError(null);
          resetRunAllFields();
        }}
      >
        <DermoscopyExceptionFields
          reason={runAllReason}
          note={runAllNote}
          onReasonChange={setRunAllReason}
          onNoteChange={setRunAllNote}
          noteRequiredButMissing={runAllNoteRequiredButMissing}
          disabled={triggerAll.isPending}
        />
      </ReasonConfirmModal>

      {/* Add image (finding fixed by the card that opened it) */}
      <Modal opened={addOpen} onClose={closeAdd} title="Add image" centered>
        <Stack gap="md">
          <FileInput
            label="Image file"
            placeholder="Choose a file"
            value={addFile}
            onChange={setAddFile}
            accept="image/jpeg,image/png,image/tiff,image/webp"
          />
          <Select
            label="Capture type"
            data={CAPTURE_TYPES}
            value={addCaptureType}
            onChange={setAddCaptureType}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeAdd}>
              Cancel
            </Button>
            <Button onClick={handleContinueToReason} disabled={!addFile || !addCaptureType}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <ReasonConfirmModal
        opened={addConfirmOpen}
        title="Confirm action"
        description="This adds a new image to this finding, attributed to this triage action."
        confirmLabel="Add image"
        isPending={addImage.isPending}
        error={addError}
        onConfirm={handleConfirmAdd}
        onClose={() => {
          setAddConfirmOpen(false);
          setAddError(null);
        }}
      />

      {/* Remove image */}
      <ReasonConfirmModal
        opened={removeTarget !== null}
        title="Confirm action"
        description={`Remove image ${removeTarget?.id ?? ''} from this case. The underlying file is not deleted.`}
        confirmLabel="Remove image"
        confirmColor="red"
        isPending={removeImage.isPending}
        error={removeError}
        onConfirm={handleConfirmRemove}
        onClose={() => {
          setRemoveTarget(null);
          setRemoveError(null);
        }}
      />

      {/* Retry image */}
      <ReasonConfirmModal
        opened={retryTarget !== null}
        title="Retry image ingestion"
        description={`Re-run ingestion for image ${retryTarget?.imageId ?? ''}. This increments the retry count.`}
        confirmLabel="Retry ingestion"
        isPending={retryImage.isPending}
        error={retryError}
        onConfirm={handleConfirmRetry}
        onClose={() => {
          setRetryTarget(null);
          setRetryError(null);
        }}
      />

      {/* Zoom */}
      <Modal
        opened={zoomOpen}
        onClose={closeZoom}
        size="xl"
        centered
        styles={{
          content: {
            display: 'flex',
            flexDirection: 'column',
            maxHeight: '92vh',
            overflow: 'hidden',
          },
          header: { flexShrink: 0 },
          body: {
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}
        title={
          zoomImage ? (
            <Stack gap={4}>
              <Group gap="xs" align="center" wrap="wrap">
                <Text size="sm" fw={600}>
                  {caseInfo.patientName}
                </Text>
                <Text size="sm" c="dimmed">
                  ·
                </Text>
                <Text size="sm">{caseInfo.orgName}</Text>
                <Text size="sm" c="dimmed">
                  ·
                </Text>
                <Text size="sm">{caseInfo.productName}</Text>
              </Group>
              <Group gap="xs" align="center">
                <Text size="xs" c="dimmed" ff="monospace">
                  {caseInfo.id}
                </Text>
                <Badge variant="light" color={zoomImage.is_primary ? 'cyan' : 'gray'}>
                  {zoomImage.capture_type}
                </Badge>
                {zoomImage.width_px && zoomImage.height_px && (
                  <Text size="sm" c="dimmed">
                    {zoomImage.width_px}×{zoomImage.height_px}
                  </Text>
                )}
              </Group>
            </Stack>
          ) : null
        }
      >
        {zoomImage && (
          <>
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
              <Image
                src={zoomImage.presigned_url}
                alt={`${zoomImage.capture_type} image ${zoomImage.id}`}
                w="100%"
              />
            </div>
            <Stack gap={4} mt="sm" data-testid="zoom-image-meta" style={{ flexShrink: 0 }}>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" w={110}>
                  Uploaded
                </Text>
                <Text size="sm">{formatDateTime(zoomImage.created_at)}</Text>
              </Group>
              <Group gap="xs" wrap="nowrap">
                <Text size="sm" c="dimmed" w={110}>
                  Uploaded by
                </Text>
                <Text size="sm">{uploaderLabel(zoomImage.uploaded_by)}</Text>
              </Group>
              <Group gap="xs" align="flex-start" wrap="nowrap">
                <Text size="sm" c="dimmed" w={110}>
                  AI processing
                </Text>
                {zoomImageReviews.length > 0 ? (
                  <Stack gap={2}>
                    <Badge variant="light" color="grape" leftSection={<IconRobot size={12} />}>
                      Used in AI review
                    </Badge>
                    {zoomImageReviews.map((r) => (
                      <Text key={r.id} size="xs" c="dimmed">
                        {formatDateTime(r.createdAt)} — {statusLabel(r.status)}
                      </Text>
                    ))}
                  </Stack>
                ) : (
                  <Text size="sm" c="dimmed">
                    Not used in AI processing
                  </Text>
                )}
              </Group>
            </Stack>
          </>
        )}
      </Modal>
    </Card>
  );
}
